<template>
  <div>
    <div v-if="isLoading" class="">loading...</div>
    <Form
      v-else
      :initial-values="initialValues"
      class="flex flex-wrap gap-y-2"
      v-slot="{ values, resetForm }"
      @submit="(values) => onSubmit(values, resetForm)"
    >
      <div class="w-full md:w-1/2 md:odd:pe-2 md:even:ps-2">
        <Field name="selectedModule" v-slot="{ field, errors: errs, handleChange }">
          <Dropdown
            :field="field"
            :label="$t('modulename')"
            :options="dropdowns.modules"
            @on-selection="(item) => handleChange(+item?.code)"
          ></Dropdown>
        </Field>
      </div>
      <div class="w-full md:w-1/2 md:odd:pe-2 md:even:ps-2">
        <Field
          name="arrServicesTransactions"
          v-slot="{ field, errors: errs, handleChange }"
          class="w-full"
        >
          <MultipleDropdownSearch
            :field="field"
            :errors="errs"
            :options="dropdowns.services"
            :label="$t('Services')"
            :placeholder="$t('Services')"
            optionValue="label"
            direction="flex-col"
            @on-selection="(item) => handleChange(item)"
          />
        </Field>
      </div>
      <div class="w-full md:w-1/2 md:odd:pe-2 md:even:ps-2">
        <Field name="employeeName" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('serviceBeneficiaryname')"
            label-class="w-full"
            class="w-full"
            direction="flex-col"
          />
        </Field>
      </div>
      <div class="w-full md:w-1/2 md:odd:pe-2 md:even:ps-2">
        <Field name="actionId" v-slot="{ field, errors: errs, handleChange }">
          <Dropdown
            :field="field"
            :label="$t('requestActions')"
            :options="dropdowns.requestActions"
            @on-selection="(item) => handleChange(+item?.code)"
          ></Dropdown>
        </Field>
      </div>
      <div class="w-full md:w-1/2 md:odd:pe-2 md:even:ps-2">
        <Field name="nationalityId" v-slot="{ field, errors: errs, handleChange }">
          <Dropdown
            :field="field"
            :errors="errs"
            :label="$t('nationality')"
            :options="countries"
            direction="flex-col"
            @on-selection="(item) => handleChange(+item?.code)"
          ></Dropdown>
        </Field>
      </div>
      <div class="w-full md:w-1/2 md:odd:pe-2 md:even:ps-2">
        <Field name="passportNumber" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('passportNumber')"
            direction="flex-col"
          />
        </Field>
      </div>
      <div class="w-full md:w-1/2 md:odd:pe-2 md:even:ps-2">
        <Field name="personEidaNumber" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('personEidaNumber')"
            direction="flex-col"
          />
        </Field>
      </div>
      <!-- date fields -->
      <div class="w-full md:w-1/2 px-2 md:px-0 flex flex-col md:flex-row gap-y-2">
        <div class="w-full md:px-2 md:w-1/2">
          <Field
            :rules="isRequired"
            name="startDate"
            v-slot="{ field, errors: errs, handleChange }"
          >
            <Datepicker
              :field="field"
              :errors="errs"
              :label="$t('requestStartDate')"
              max-date="__now__"
              @on-change="(date) => handleChange(date)"
            >
            </Datepicker>
          </Field>
        </div>
        <div class="w-full md:odd:pe-2 md:even:ps-2 md:w-1/2">
          <Field :rules="isRequired" name="endDate" v-slot="{ field, errors: errs, handleChange }">
            <Datepicker
              :field="field"
              :errors="errs"
              :label="$t('requestEndDate')"
              max-date="__now__"
              @on-change="(date) => handleChange(date)"
            >
            </Datepicker>
          </Field>
        </div>
      </div>
      <fieldset class="flex-1 border-2 border-primary rounded p-4 flex flex-col gap-y-5">
        <legend class="px-4 text-xl">{{ $t('searchForRequest') }}</legend>
        <div class="flex flex-col gap-y-2">
          <div class="flex gap-x-4">
            <Field name="searchId" v-slot="{ field, errors: errs, handleChange }">
              <Dropdown
                :field="field"
                :errors="errs"
                :options="searchCriteria"
                direction="flex-col"
                class="w-2/6"
                @on-selection="(item) => handleChange(+item?.value)"
              ></Dropdown>
            </Field>

            <Field
              v-if="values.searchId"
              name="requestOptionValue"
              v-slot="{ field, errors: errs }"
            >
              <TextInput :field="field" :errors="errs" class="flex-1" direction="flex-col" />
            </Field>
          </div>
          <div class="flex justify-end">
            <button
              class="text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
            >
              <div class="p-2 bg-primary rounded-s-md">
                <faMagnifyingGlass class="w-4 fill-white"></faMagnifyingGlass>
              </div>
              <span class="w-full text-center">
                {{ $t('Search') }}
              </span>
              <div class="p-0.5"></div>
            </button>
          </div>
        </div>
      </fieldset>
    </Form>
  </div>
</template>

<script setup>
import faMagnifyingGlass from '@/assets/icons/faMagnifyingGlass.svg'
import Datepicker from '@/components/SimpleForm/Datepicker.vue'
import Dropdown from '@/components/SimpleForm/Dropdown.vue'
import MultipleDropdownSearch from '@/components/SimpleForm/MultiSelect.vue'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import axiosInstance from '@/services'
import { useLookupsStore } from '@/stores/lookups'
import { Field, Form } from 'vee-validate'
import { onMounted, reactive, ref, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import client_lookups from '@/assets/json/clientLookups.json'
import { useRoute } from 'vue-router'
import { initial } from 'lodash'

const route = useRoute()
const lookupsStore = useLookupsStore()
const i18n = useI18n()

const emit = defineEmits('onSubmit')

const {
  queryType: { receiptnumber, bankRefernaceNumber, requestNumber }
} = client_lookups

const props = defineProps({
  isReturned: Boolean
})

const isLoading = ref(true)
const initialValues = reactive({
  selectedModule: null,
  servicesTransactions: [],
  requestActions: null
})

const paginatorConfig = reactive({
  start: 0,
  totalItemCount: 0,
  number: 10, // page size
  numberOfPages: 3,
  pageNumber: 1, // (start/pageSize) + 1
  searchId: null
})

const searchCriteria = [
  {
    label: i18n.t('requestNumber'),
    value: 1
  },
  {
    label: i18n.t('receiptnumber'),
    value: 2
  },
  {
    label: i18n.t('bankRefernaceNumber'),
    value: 3
  }
]

const dropdowns = reactive({
  services: [],
  modules: []
})

async function fetchServices() {
  const response = await axiosInstance.get('/api/modules')
  console.info('fetchServices')

  response.forEach((module) => {
    module.children.forEach((parentService) => {
      parentService.children.forEach((subService) => {
        subService.children.forEach((transaction) => {
          const serviceName = [
            module.label,
            parentService.label,
            subService.label,
            transaction.label
          ].join('-')

          dropdowns.services.push({
            label: serviceName,
            value: transaction.id.replaceAll('t_', ''),
            code: transaction.id.replaceAll('t_', '')
          })
        })
      })
    })
  })
}

async function fetchModules() {
  const response = await axiosInstance.get('/api/application/modules')

  dropdowns.modules = response.map((r) => {
    return {
      ...r,
      label: r.text,
      value: r.id
    }
  })
}

onMounted(async () => {
  await fetchServices()
  await fetchModules()
  dropdowns.requestActions = await lookupsStore.fetchLookup('/api/lookup/requestActions')
  dropdowns.countries = await lookupsStore.fetchLookup('/api/lookup/countries')

  if ('requestNumber' in route.query) {
    initialValues.searchId = requestNumber
    initialValues.requestOptionValue = route.query.requestNumber
  }

  await onSubmit(initialValues)
  isLoading.value = false
})

async function onSubmit(form, resetForm) {
  const payload = {
    ...form,
    pageSize: paginatorConfig.number,
    currentPage: paginatorConfig.pageNumber,
    loadLookupDataFromCache: false
  }

  const { searchId, requestOptionValue } = form

  if (searchId == receiptnumber) {
    payload.receiptNumber = requestOptionValue
  } else if (searchId == bankRefernaceNumber) {
    payload.bankReferanceId = requestOptionValue
  } else if (searchId == requestNumber) {
    payload.requestNumber = requestOptionValue
  }

  emit('onSubmit', payload)
}
</script>

<style lang="scss" scoped></style>
